//
// Return an Image or Video player from Airtable field
//
import React from 'react';
import {ActivityIndicator, Image, View} from 'react-native';
import YoutubePlayer from 'react-native-youtube-iframe';
import InViewPort from '@coffeebeanslabs/react-native-inviewport';

import {AirtableAttachmentDetail, AirtableFeedItem} from '../../models/airtable';

import {ArticleVideoType, GraphicContentType} from './constants';
import styles from './styles';

export const getFeedDiscoverTileImage = (
  detail: AirtableAttachmentDetail | undefined,
  _: boolean,
  visibleCallback?: (isVisible: boolean) => void,
  _isVirtualized = false,
  // Native single-active-player flag; web autoplays all videos (browser-cached).
  _isActive = false,
) => {
  if (!detail) return null;

  if (detail.type?.indexOf('image') === 0) {
    // detail.url is the middleware-optimized feed image: as of DIS-1338 the
    // /content/feed payload serves a 480px WebP variant (/media/<token>/480.webp),
    // server-side resize+transcode — this is the fix for the heavy per-scroll
    // transfer. Images are cacheable (cache-control: public, max-age=86400) in
    // production; the "no cache" once seen in DevTools was the Disable-cache toggle.
    return (
      <View style={styles.tileImageContainer}>
        <Image
          source={{uri: detail.url}}
          style={[
            styles.tileImage,
            // @ts-expect-error 'detail.width' is possibly 'undefined'.
            {aspectRatio: detail.width / detail.height},
          ]}
          resizeMode="cover"
        />
      </View>
    );
  } else if (detail.type === 'video') {
    const innerComponent = () => (
      <video muted style={styles.tileVideo} autoPlay loop playsInline>
        <source src={detail.url} />
      </video>
    );
    // Using InViewPort with multiple videos on the screen will overwhelm
    // the UIManager with callbacks and lock up devices. Easier to just let them
    // all play
    return visibleCallback ? (
      <InViewPort onChange={visibleCallback}>{innerComponent()}</InViewPort>
    ) : (
      innerComponent()
    );
  }

  return null;
};

/**
 * Web detail video (DIS-1536, aspect mirror). Derives the true aspect from the loaded
 * metadata so a portrait clip fits its aspect instead of letterboxing in the 16:9 box.
 * autoplay/recycle parity are out of scope on web — the `autoPlay` wiring is unchanged.
 */
const WebDetailVideo = ({
  detail,
  autoPlay,
  loop,
}: {
  detail: AirtableAttachmentDetail;
  autoPlay: boolean;
  loop: boolean;
}) => {
  const [aspectRatio, setAspectRatio] = React.useState<number | undefined>(undefined);
  // Covers the <video> until metadata resolves the true aspect, so the clip never snaps
  // from the neutral 16:9 default to its real ratio on screen (DIS-1536 QA). Mirrors the
  // native DetailVideo loader.
  const [aspectResolved, setAspectResolved] = React.useState(false);
  return (
    <>
      <video
        controls
        autoPlay={autoPlay}
        muted
        style={aspectRatio ? {...styles.video, aspectRatio} : styles.video}
        loop={loop}
        onLoadedMetadata={e => {
          setAspectResolved(true);
          const {videoWidth, videoHeight} = e.currentTarget;
          if (videoWidth && videoHeight) setAspectRatio(videoWidth / videoHeight);
        }}
        onError={() => setAspectResolved(true)}>
        <source src={detail.url} />
      </video>
      {!aspectResolved && (
        <View testID="detail-video-loading" style={styles.videoLoadingOverlay}>
          <ActivityIndicator color="#fff" />
        </View>
      )}
    </>
  );
};

export const getFeedDiscoverDetailGraphics = (
  detail: AirtableAttachmentDetail | undefined,
  vidType: string | null,
  margin?: boolean,
  source?: AirtableFeedItem | null,
  setHeadlineVideoPaused = (_: boolean) => null,
  headlineVideoPaused = false,
  articleVideoPaused = true,
  youtubeVideoPaused = true,
) => {
  if (!detail) return null;

  if (detail.type.indexOf(GraphicContentType.Image) > -1) {
    return (
      <View style={!margin ? styles.imageContainer : [styles.imageContainer, styles.topMargin]}>
        <Image
          source={{uri: detail.url}}
          style={[
            styles.imageResponsive,
            // @ts-expect-error 'detail.width' is possibly 'undefined'.
            {aspectRatio: detail.width / detail.height},
          ]}
          resizeMode="cover"
        />
      </View>
    );
  } else if (detail.type === GraphicContentType.Video) {
    let setter = (_?: boolean) => null;
    let getter = true;

    if (vidType === ArticleVideoType.HeadlineVideo) {
      setter = headlineImageVisible => setHeadlineVideoPaused(!headlineImageVisible);
      getter = headlineVideoPaused;
    } else if (vidType === ArticleVideoType.ArticleVideo) {
      setter = () => null;
      getter = articleVideoPaused;
    } else if (vidType === ArticleVideoType.YoutubeVideo) {
      setter = () => null;
      getter = youtubeVideoPaused;
    }

    if (vidType !== ArticleVideoType.YoutubeVideo) {
      return (
        <InViewPort onChange={(isVisible: boolean) => setter(isVisible)}>
          <View style={!margin ? styles.videoContainer : [styles.videoContainer, styles.topMargin]}>
            <WebDetailVideo
              detail={detail}
              autoPlay={getter}
              loop={vidType === ArticleVideoType.HeadlineVideo}
            />
          </View>
        </InViewPort>
      );
    } else {
      const youtubeId = source?.youtube
        ?.replace('https://youtu.be/', '')
        .replace('https://www.youtube.com/watch?v=', '');
      return (
        <InViewPort onChange={(isVisible: boolean) => setter(isVisible)}>
          <View style={!margin ? styles.videoContainer : [styles.videoContainer, styles.topMargin]}>
            <YoutubePlayer play={!getter} videoId={youtubeId} webViewStyle={styles.youtube} />
          </View>
        </InViewPort>
      );
    }
  }

  return null;
};
